
import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import moment from "moment";
import toast from "react-hot-toast";

import {
  EPATConstant,
  IntakeSheetSession,
  CurrentProviderData,
  RoleType,
} from "../../../helpers/constant";
import "./EpatConsent.css";
import { GragIntakeSheetData } from "../assignmentOfBenefit/Service";
import { getSessionStorage } from "../../../helpers/storage";
import { GetCaseFormListData, SaveCaseFormData} from "../medicalLien/services3";
import SignaturePad from "../../../Component/shared/signaturePad";
import SpinnerLoader from "../../../Component/shared/loader/spinnerLoader/SpinnerLoader";
import { CallApiForProviderList } from "../IntakeSheetUtils";
import { usePatientData } from "../../../commonFunction/storage/Patientdatacontext";


const SHARED_SIG_KEY = "sharedPatientSignature";

const EPAT = forwardRef(function EPAT(props, ref) {
  const {
    providerOptions = [],
    sessionData = {},
    signatureMiss,
    copySignature,
  } = props;

  const tokenData = JSON.parse(sessionStorage.getItem("tokenData") || "{}");
  const providerData = getSessionStorage(CurrentProviderData) || [];
  const roleName = sessionData?.Role?.Name?.trim().toLowerCase() ?? "";
  const isFrontDesk =
    roleName === "frontdesk" || roleName === "front desk" || roleName === "admin";

  const [loading, setLoading] = useState(false);
  const [CaseFormDataId, setCaseFormDataId] = useState("");
  const [collectionOfEPAT, setCollectionOfEPAT] = useState([]);
  const [BeforeParsingEPATData, setBeforeParsingEPATData] = useState([]);

  const [name, setName] = useState("");
  const [providerName, setProviderName] = useState("");
  const [providerAddress, setProviderAddress] = useState("");
  const [patientSignImg, setPatientSignImg] = useState("");
  const [signDate, setSignDate] = useState(new Date());
  const [selectedProvider, setSelectedProvider] = useState(providerOptions[0] || null);
  const [sigPadOpen, setSigPadOpen] = useState(false);

    const { patientData, fetchPatientData } = usePatientData();


  // ── Imperative handle – includes applySignature ───────────────────────────
  useImperativeHandle(ref, () => ({
    save:           () => saveFunction(true),
    isPending:      () => checkIsPending(),
      getSignature:   () => patientSignImg || "",
    applySignature: (sig) => {
      if (!sig) return;
      setPatientSignImg(sig);
      setSignDate(new Date());
    },
  }));

  const checkIsPending = () => !patientSignImg;

  useEffect(() => {
    props?.onReady?.("epat");
  }, [patientSignImg]);

  const callApi = async () => {
    setLoading(true);
    const formData = await handleGetCaseFormData();
    const patientInfo = patientData || await fetchPatientData(tokenData?.caseId, tokenData?.companyId);
    const provData = await CallApiForProviderList(
      tokenData?.companyId,
      tokenData?.providerId,
    );
    if (!formData?.JsonData) {
      setLoading(false);
      return;
    }
    const epatData = JSON.parse(formData.JsonData);
    const collection = GragIntakeSheetData(epatData);
    setCollectionOfEPAT(collection);
    setBeforeParsingEPATData(epatData);
    setCaseFormDataId(formData.CaseFormDataId);
    bindValueToControl(collection, patientInfo, provData);
    setLoading(false);
  };

  useEffect(() => { callApi(); }, [patientData]);

  const handleGetCaseFormData = async () => {
    try {
      const requestBody = {
        CaseFormDataId: null,
        CaseId: tokenData.caseId,
        CompanyId: tokenData.companyId,
        DoctorId: "ALL",
        EntityLevel: 0,
        FormId: 7,
        FormName: "EPAT Consent",
        FormType: null,
        IsFormCompleted: false,
        JsonData: null,
        TemplateName: null,
        UserId: tokenData?.userId,
      };
      const response = await GetCaseFormListData(requestBody);
      if (response?.HasException) {
        toast.error(`Something went wrong. ${response?.MessageText}. ${response?.Exception}`);
        return {};
      }
      return response?.Data?.[0] || {};
    } catch (error) {
      toast.error(error.message);
      return {};
    }
  };


  const CallSaveApiForIntakeSheet = async (jsonData) => {
    try {
      setLoading(true);
      const payload = {
        CaseFormDataId: "48490",
        CaseId: tokenData.caseId,
        CompanyId: tokenData.companyId,
        DoctorId: tokenData.doctorId || tokenData.DoctorId || "",
        EntityLevel: 0,
        FormId: 7,
        FormName: "EPAT Consent",
        FormType: null,
        IsChanged: false,
        IsCopySign: false,
        IsFormCompleted: false,
        TemplateName: null,
        UserId: tokenData?.userId,
        officeId: tokenData?.providerId,
        JsonData: jsonData,
      };
      const response = await SaveCaseFormData(payload);
      if (response?.HasException) {
        toast.error(`Error while saving EPAT Consent. ${response?.MessageText || ""}`);
        return false;
      } else {
        if (response?.Data?.[0]?.CaseFormDataId) {
          setCaseFormDataId(response.Data[0].CaseFormDataId);
        }
        toast.success("EPAT Consent saved successfully.");
        return true;
      }
    } catch (err) {
      toast.error("Error while saving EPAT Consent data.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const createProviderInfoView = (provData) => {
    const { Address, Street, City, State, Zip } = provData;
    return [Address, Street, City, State, Zip].filter(Boolean).join(", ");
  };

  const bindValueToControl = (collection, patientInfo, provData) => {
    if (!patientInfo) return;

    const filterData = (key) => {
      const item = collection.find((x) => x.Name === key);
      const val = item ? item.Value : "";
      return val === "01/01/1900" ? "" : val;
    };

    const normalise = (raw) =>
      !raw
        ? ""
        : raw.includes(IntakeSheetSession.dataBlock)
          ? raw
          : IntakeSheetSession.dataBlock + raw;

    const parseDateSafe = (raw) =>
      isNaN(new Date(raw).getTime()) ? new Date() : new Date(raw);

    const fullName = [
      patientInfo.patientFirstName,
      patientInfo.patientMiddleName,
      patientInfo.patientLastName,
    ].filter(Boolean).join(" ");

    if (isFrontDesk) {
      const provNameRaw = filterData(EPATConstant.ProviderName) || providerOptions[0]?.label || "";
      const provider = providerOptions.find((x) => x.label === provNameRaw) || providerOptions[0] || null;
      setSelectedProvider(provider);
      setProviderName(provider?.label || "");
      setProviderAddress(createProviderInfoView(provData));
    } else {
      const resolvedProvName =
        filterData(EPATConstant.ProviderName) ||
        provData[0]?.ProviderName ||
        tokenData?.providerName ||
        tokenData?.ProviderName ||
        "";
      setProviderName(resolvedProvName);
      setProviderAddress(createProviderInfoView(provData));
    }

    if (fullName) setName(fullName);

    // If copySignature is ON and a shared sig exists, prefer it
    const savedSig = copySignature ? sessionStorage.getItem(SHARED_SIG_KEY) : null;
    const resolvedSign = savedSig || normalise(filterData(EPATConstant.PatientSign));

    setPatientSignImg(resolvedSign);
    setSignDate(parseDateSafe(filterData(EPATConstant.PatientSignDate)));

    if (!resolvedSign) signatureMiss?.(true);
    setLoading(false);
  };

  const updateCollection = (collection) => {
    const updated = [...collection];
    const set = (key, value) => {
      const item = updated.find((x) => x.Name === key);
      if (item) item.Value = value;
    };
    const fmt = (d) =>
      d == null || d === "" ? "" : moment(d).format("MM-DD-YYYY").split("-").join("/");

    const provVal =
      isFrontDesk && providerOptions.length === 1
        ? providerOptions[0]?.label || ""
        : providerName || selectedProvider?.label || "";

    set(EPATConstant.PatientName, name);
    set(EPATConstant.PatientSign, patientSignImg);
    set(EPATConstant.PatientSignDate, fmt(signDate));
    set(EPATConstant.ProviderName, provVal);
    set(EPATConstant.ProviderAddress, providerAddress);
    return updated;
  };

  const formJsonRequest = async (updatedCollection, beforeData) => {
    try {
      for (let i = 0; i < beforeData.notesdata.length; i++) {
        const filterDataArr = updatedCollection.filter(
          (x) => x.RenderKeyName === beforeData.notesdata[i].RenderKeyName,
        );
        if (!filterDataArr.length) continue;
        for (let j = 0; j < beforeData.notesdata[i].ApplicationValue.length; j++) {
          if (beforeData.notesdata[i].ApplicationValue[j].Name !== filterDataArr[j]?.Name) continue;
          beforeData.notesdata[i].ApplicationValue[j].Type    = filterDataArr[j].Type;
          beforeData.notesdata[i].ApplicationValue[j].isRender = filterDataArr[j].isRender;
          const isSign = beforeData.notesdata[i].ApplicationValue[j].Name === EPATConstant.PatientSign;
          if (isSign) {
            const val = filterDataArr[j].Value;
            if (val) {
              beforeData.notesdata[i].ApplicationValue[j].Value = val.includes(";")
                ? val.split(";")[1].split(",")[1]
                : val;
            } else {
              beforeData.notesdata[i].ApplicationValue[j].Value = "";
            }
          } else {
            beforeData.notesdata[i].ApplicationValue[j].Value = filterDataArr[j].Value;
          }
        }
      }
      const result = await CallSaveApiForIntakeSheet(JSON.stringify(beforeData));
      setCollectionOfEPAT(updatedCollection);
      setBeforeParsingEPATData(beforeData);
      return result;
    } catch (error) {
      console.error(error?.message);
    }
  };

  const saveFunction = async () => {
    setLoading(true);
    const updatedCollection = updateCollection(collectionOfEPAT);
    const beforeDataClone = JSON.parse(JSON.stringify(BeforeParsingEPATData));
    const result = await formJsonRequest(updatedCollection, beforeDataClone);
    setLoading(false);
    return result;
  };

  const clickPatientSign = () => setSigPadOpen(true);

  // ── Save sig to sessionStorage so other tabs can copy it ─────────────────
  const signUpdatedCall = (base64Img) => {
    const newBase = base64Img.includes(IntakeSheetSession.dataBlock1)
      ? base64Img.replace(IntakeSheetSession.dataBlock1, IntakeSheetSession.dataBlock)
      : base64Img;
    setPatientSignImg(newBase);
    setSignDate(new Date());
    sessionStorage.setItem(SHARED_SIG_KEY, newBase);   // ← persist for copy
  };

  const clearSignCall = () => {
    setPatientSignImg("");
    setSignDate("");
    signatureMiss?.(true);
  };

  const ProviderField = () =>
    providerOptions.length === 1 ? (
      <input className="epat__input" type="text" value={providerOptions[0]?.label || ""} readOnly />
    ) : (
      <Select
        options={providerOptions}
        value={selectedProvider}
        getOptionLabel={(e) => e?.label}
        getOptionValue={(e) => e?.value}
        tabSelectsValue={false}
        classNamePrefix="react-select"
        placeholder="Select Provider"
      />
    );

  return (
    <>
      {loading ? (
        <SpinnerLoader message="Loading form data…" />
      ) : (
        <div className="epat__wrapper">
          <div className="epat__title-block">
            <div className="epat__title-main">EPAT CONSENT</div>
          </div>
          <div className="epat__consent-sections">
            <div className="epat__consent-block">
              <div className="epat__consent-heading">Cooperation with treatment:</div>
              <p>In order for Shock Wave Therapy treatment to be effective, I must come to scheduled appointments unless there are unforeseen circumstances. I understand and agree to cooperate with and perform the home physical therapy program intended for me. If I have trouble with any part of my treatment program, I will discuss it with my provider.</p>
            </div>
            <div className="epat__consent-block">
              <div className="epat__consent-heading">No Warranty:</div>
              <p>I understand that there are no guarantees regarding a cure for or improvement in my condition. I understand that provider will outline and discuss goals of Shock Wave Therapy treatment for my condition and will discuss treatment options with me before I consent to treatment.</p>
            </div>
            <div className="epat__consent-block">
              <div className="epat__consent-heading">Informed consent for treatment:</div>
              <p>The term "informed consent" means that the potential risks, benefits have been explained to me.</p>
            </div>
            <div className="epat__consent-block">
              <div className="epat__consent-heading">Potential Risks:</div>
              <p>Side effects from EPAT or Shockwave therapy are limited to mild bruising, swelling, pain, numbness or tingling in the treated area, and the recovery is minimal compared with that of surgical intervention. This discomfort is usually temporary; if it does not subside in a reasonable time period, I agree to contact my physician.</p>
            </div>
            <div className="epat__consent-block">
              <div className="epat__consent-heading">Potential benefits:</div>
              <p>I may experience an improvement in my symptoms and an increase in my ability to perform daily activities. I may experience increase strength, awareness, flexibility, and endurance in my movements. I may experience decreased pain and discomfort. I should gain a greater knowledge about managing my condition and the resources available to me.</p>
            </div>
            <div className="epat__consent-block">
              <div className="epat__consent-heading">Alternatives:</div>
              <p>If you do not wish to participate in the EPAT therapy and shock wave treatment program, I will discuss with my medical, surgical or pharmacological alternatives with my physician or primary care provider.</p>
            </div>
            <div className="epat__consent-block">
              <div className="epat__consent-heading">Payment:</div>
              <p>I understand that I am responsible for any charges not covered by insurance.</p>
            </div>
          </div>
          <div className="epat__fields">
            <div className="epat__field-row">
              <span className="epat__field-label"><b>Name Of Provider:</b></span>
              <div className="epat__field-control">
                {isFrontDesk ? <ProviderField /> : (
                  <input className="epat__input" type="text" value={providerName || providerData[0]?.ProviderName || ""} readOnly disabled />
                )}
              </div>
            </div>
            <div className="epat__field-row">
              <span className="epat__field-label"><b>Address Of Provider:</b></span>
              <div className="epat__field-control">
                <textarea className="epat__textarea" value={providerAddress} disabled />
              </div>
            </div>
            <div className="epat__field-row">
              <span className="epat__field-label"><b>Name Of Patient:</b></span>
              <div className="epat__field-control">
                <input className="epat__input" type="text" value={name} onChange={(e) => setName(e.target.value)} disabled />
              </div>
            </div>
            <div className="epat__field-row epat__field-row--top">
              <span className="epat__field-label"><b>Signature :</b></span>
              <div className="epat__field-control">
                <div
                  className={`epat__sign-box${!patientSignImg ? " epat__sign-box--empty" : ""}`}
                  onClick={clickPatientSign}
                >
                  {patientSignImg ? (
                    <img src={patientSignImg} alt="Patient signature" className="epat__sign-img" />
                  ) : (
                    <span className="epat__sign-hint">Click to sign</span>
                  )}
                </div>
              </div>
            </div>
            <div className="epat__field-row">
              <span className="epat__field-label"><b>Signature Date:</b></span>
              <div className="epat__field-control">
                <DatePicker
                  selected={signDate}
                  onChange={(d) => setSignDate(d)}
                  dateFormat="MM/dd/yyyy"
                  className="epat__datepicker"
                  showYearDropdown showMonthDropdown dropdownMode="select"
                />
              </div>
            </div>
          </div>
          <SignaturePad
            isOpen={sigPadOpen}
            title="Patient Signature"
            existingSign={patientSignImg}
            onClose={() => setSigPadOpen(false)}
            onSave={(base64) => { signUpdatedCall(base64); setSigPadOpen(false); }}
            onClear={clearSignCall}
          />
        </div>
      )}
    </>
  );
});

export default EPAT;